import React from "react";
import { Controller, useFormContext } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { InboundWebhookConnectionForm } from "src/inboundWebhookNode/types";

type FieldMappingProps = {
  dataLoc?: string;
  runFieldErrors: FieldErrors | undefined;
  immutable: boolean;
  title: string;
  helpText: string;
  fieldPrefix: string;
} & (
  | {
      fieldIdKey: "correlation_id.id";
      fieldExpressionKey: "correlation_id.expression";
    }
  | {
      fieldIdKey: "response.id";
      fieldExpressionKey: "response.mapped_to";
    }
);

const FieldMapping: React.FC<FieldMappingProps> = ({
  dataLoc,
  runFieldErrors,
  immutable,
  title,
  helpText,
  fieldPrefix,
  fieldIdKey,
  fieldExpressionKey,
}) => {
  const formProps = useFormContext<InboundWebhookConnectionForm>();
  const runFieldError: string | undefined =
    runFieldErrors?.[formProps.getValues(fieldIdKey)];
  return (
    <>
      <h3 className="text-gray-800 font-inter-medium-12px">{title}</h3>
      <span className="text-gray-500 font-inter-normal-12px">{helpText}</span>
      <Card className="mt-2.5 flex w-full flex-row items-center">
        <div className="mr-2 flex text-gray-700 font-inter-normal-12px">
          {fieldPrefix}
        </div>
        <Controller
          control={formProps.control}
          name={fieldExpressionKey}
          render={({ field }) => (
            <AutocompleteCodeInput
              dataLoc={dataLoc}
              disabled={immutable}
              error={runFieldError}
              prefix="data."
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Card>
    </>
  );
};

type Props = { runFieldErrors: FieldErrors | undefined; immutable: boolean };

export const RequestMapping: React.FC<Props> = ({
  runFieldErrors,
  immutable,
}) => {
  return (
    <FieldMapping
      dataLoc="in-webhook-node-request-field-mapping"
      fieldExpressionKey="correlation_id.expression"
      fieldIdKey="correlation_id.id"
      fieldPrefix="Identifier"
      helpText="Specify where the identifier can be found in the data object. This is the same identifier set during the Connection configuration"
      immutable={immutable}
      runFieldErrors={runFieldErrors}
      title="Which field in the data object contains an identifier?"
    />
  );
};

export const ResponseMapping: React.FC<Props> = ({
  runFieldErrors,
  immutable,
}) => {
  return (
    <FieldMapping
      dataLoc="in-webhook-node-response-field-mapping"
      fieldExpressionKey="response.mapped_to"
      fieldIdKey="response.id"
      fieldPrefix="Output location"
      helpText="Add the entire webhook response into the data object"
      immutable={immutable}
      runFieldErrors={runFieldErrors}
      title="Set output location"
    />
  );
};
